import React from 'react';
import ReactDOM from 'react-dom';
import DeckGL from "deck.gl";
import {WebMercatorViewport} from 'deck.gl';
import {GeoJsonLayer, IconLayer, TextLayer} from 'deck.gl'
import Map from "react-map-gl";
import "mapbox-gl/dist/mapbox-gl.css";

import { Button, Text, Box, ActionIcon, Tooltip } from '@mantine/core';
import { IconEdit, IconReportAnalytics, IconTrash } from '@tabler/icons-react';

import custom_pin from "../../images/pins/pin.png";

import {useDebounce} from 'react-use';

import autoBind from 'react-autobind';
import { If, Then, Else } from 'react-if'

import {
  withScriptjs,
  withGoogleMap,
  GoogleMap,
  Rectangle,
  InfoWindow,
} from "react-google-maps";
import MarkerWithLabel from "react-google-maps/lib/components/addons/MarkerWithLabel"

// import other components within the same project here
import Config from "../../Config.jsx";
import Localization from "../../Localization.jsx"

class LocaleMapView extends React.Component {
  constructor(props) {
    super(props);
    this.refs = React.createRef();

    // state variables here
    this.state = {
      map: {
        zoom: 0,
        center: {
          lat: 0,
          lon: 0
        },
        bounds:{
          ul:{
            lat: 0,
            lon: 0
          },
          br:{
            lat: 0,
            lon: 0
          }
        },
        style: Config.MBOX_STYLE(Localization.strings.getLanguage()).mono,
        _style: "mono",
        lang: "zh"
      },
      // localeMarkerID: -1,
      width: 0,
      height: 0,
      popup_data: {
        show: false,
        lat: 0,
        lon: 0,
        meta: null,
        index: -1
      },
      webGLSupported: true // Track WebGL support
    };
    autoBind(this);

    // Check WebGL support synchronously. setState is a no-op in the constructor, so the
    // initial state must be assigned directly or the "WebGL not supported" fallback never
    // renders on devices without WebGL.
    this.state.webGLSupported = this.detectWebGLSupport();
  }

  initializeBoundsAndRefresh() {
    const { parent } = this.props;
    const { localeMapState } = parent.refs;
    
    const viewState = {
        longitude: localeMapState.state.center.lng,
        latitude: localeMapState.state.center.lat,
        zoom: localeMapState.state.zoom,
        width: this.state.width,
        height: this.state.height
    };

    const viewport = new WebMercatorViewport(viewState);
    const ul = viewport.unproject([0, 0]);
    const br = viewport.unproject([this.state.width, this.state.height], false);
    
    const bounds = {
        ul: { lon: ul[0], lat: ul[1] },
        br: { lon: br[0], lat: br[1] }
    };

    if (localeMapState) {
        localeMapState.setState({ bounds: bounds });
        if(localeMapState.state.bounds === null){
          localeMapState.state.bounds = bounds;
        }
    }
    
    parent.refreshLocaleList();
  }

  detectWebGLSupport() {
    try {
      const canvas = document.createElement('canvas');
      // deck.gl 9 requires WebGL2, so probing WebGL1 would pass in browsers
      // where only WebGL2 is blocked and the map would crash past the fallback.
      const gl = canvas.getContext('webgl2');
      // Release the probe context right away: browsers cap live WebGL contexts
      // (~16 in Chrome), and a leaked probe per mount can starve the real maps.
      gl?.getExtension('WEBGL_lose_context')?.loseContext();
      return !!gl;
    } catch (e) {
      console.error('WebGL detection error:', e);
      return false;
    }
  }

  checkWebGLSupport() {
    this.setState({ webGLSupported: this.detectWebGLSupport() });
  }

  componentDidMount() {
    this.updateWindowDimensions();
    window.addEventListener('resize', this.updateWindowDimensions);
    // Initialize map state from props
    this.syncMapStateWithProps();
  }

  componentDidUpdate(prevProps, prevState) {
    // Check if language changed
    if (Localization.strings.getLanguage() !== this.state.map.lang) {
      const newLang = Localization.strings.getLanguage();
      let newStyle;
      let newStyleType = this.state.map._style;
      
      if (this.state.map._style === "mono") {
        newStyle = Config.MBOX_STYLE(newLang).mono;
      } else if (this.state.map._style === "satellite") {
        newStyle = Config.MBOX_STYLE(newLang).satellite;
      } else if (this.state.map._style === "cartoon") {
        newStyle = Config.MBOX_STYLE(newLang).cartoon;
      }
      
      this.setState({
        map: {
          ...this.state.map,
          lang: newLang,
          style: newStyle,
          _style: newStyleType
        }
      });
    }
    
    if (prevState && prevState.height === 0 && this.state.height !== 0) {
      this.initializeBoundsAndRefresh();
    }
    
    // Check if map center or zoom changed
    const currentZoom = this.props.parent.refs.localeMapState.state.zoom;
    const centerLng = this.props.parent.refs.localeMapState.state.center.lng;
    const centerLat = this.props.parent.refs.localeMapState.state.center.lat;
    
    if (currentZoom !== this.state.map.zoom || 
        centerLng !== this.state.map.center.lon || 
        centerLat !== this.state.map.center.lat) {
      this.syncMapStateWithProps();
    }
  }
  
  syncMapStateWithProps() {
    // Sync map state with props
    this.setState({
      map: {
        ...this.state.map,
        zoom: this.props.parent.refs.localeMapState.state.zoom,
        center: {
          lon: this.props.parent.refs.localeMapState.state.center.lng,
          lat: this.props.parent.refs.localeMapState.state.center.lat
        }
      }
    });
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateWindowDimensions);
    clearTimeout(this.dragEndTimeout);
  }

  updateWindowDimensions() {
    let boundingRect = this.props.parent.refs.localeViewerBar.refs.div.getBoundingClientRect();
    let height = window.innerHeight - 64; // Account for TopBar height
    this.setState({
      width: window.innerWidth,
      height: height,
    });
    this.state.width = window.innerWidth;
    this.state.height = height;
  }

  localeMarkerClicked(locale) {
    let position = {
      lat: parseFloat(locale['mid_lat']),
      lng: parseFloat(locale['mid_lon'])
    };
    this.props.parent.updateMapState();
  }

  onMapMounted = (map) => {
    this.state.map = map;
  }

  onZoomChanged() {
    let zoom = this.state.map.getZoom();
    this.props.parent.refreshLocaleList();
  }

  onBoundsChanged() {
    let bounds = this.state.map.getBounds();
    this.props.parent.refs.localeMapState.setState({ bounds: bounds });
    this.props.parent.refs.localeMapState.state.bounds = bounds;
  }

  onDragEnd() {
    this.props.parent.refreshLocaleList();
    // this.setState({ localeMarkerID: -1 });
    // this.state.localeMarkerID = -1;
  }

  onMapTypeIdChanged() {
    let mapType = this.state.map.getMapTypeId();
    this.props.parent.refs.localeMapState.setState({ mapTypeId: mapType });
    this.props.parent.refs.localeMapState.state.mapTypeId = mapType;
  }

  checkWithinMapBounds(lat, lng) {
    let result = false;
    let bounds = this.props.parent.refs.localeMapState.state.bounds;
    if (bounds != null) {
      if(lat >= bounds.getSouthWest().lat() && lat <= bounds.getNorthEast().lat() && lng >= bounds.getSouthWest().lng() && lng <= bounds.getNorthEast().lng()) {
        result = true;
      }
    }
    return result;
  }

  infoWindowCloseClicked() {
    this.props.parent.updateMapState();
    // this.setState({ localeMarkerID: -1 });
    // this.state.localeMarkerID = -1;
  }

  editLocaleClicked(index) {
    this.props.parent.editLocaleByIndex(index);
  }

  retrieveAnswerClicked(index) {
    this.props.parent.retrieveAnswerByIndex(index);
  }

  deleteLocaleClicked(id) {
    this.props.parent.deleteLocale(id);
  }

  genBoxData(){
    // Safety check: ensure parent state is available
    if (!this.props.parent || !this.props.parent.state || !this.props.parent.state.localeListInMap) {
      return new GeoJsonLayer({
        id: 'geojson-layer',
        data: [],
        pickable: true,
        stroked: false,
        filled: true,
        extruded: true,
        lineWidthScale: 20,
        lineWidthMinPixels: 2,
        getFillColor: [160, 160, 180, 200],
        getLineColor: d => [Math.sqrt(d.exits), 140, 0],
        getRadius: 100,
        getLineWidth: 1,
        getElevation: 30
      });
    }
    
    var boxData = []
    for(var i = 0; i<this.props.parent.state.localeListInMap.length; i++){
      boxData.push({
        'type': 'Feature',
        'properties': {
          name: this.props.parent.state.localeListInMap[i].name
        },
        'color': parseInt(this.props.parent.state.localeListInMap[i].color).toString(16),
        'geometry': {
          'type': 'Polygon',
          'coordinates': [[
            [parseFloat(this.props.parent.state.localeListInMap[i].ul_lon), parseFloat(this.props.parent.state.localeListInMap[i].ul_lat)],
            [parseFloat(this.props.parent.state.localeListInMap[i].ul_lon), parseFloat(this.props.parent.state.localeListInMap[i].br_lat)],
            [parseFloat(this.props.parent.state.localeListInMap[i].br_lon), parseFloat(this.props.parent.state.localeListInMap[i].br_lat)],
            [parseFloat(this.props.parent.state.localeListInMap[i].br_lon), parseFloat(this.props.parent.state.localeListInMap[i].ul_lat)],
            [parseFloat(this.props.parent.state.localeListInMap[i].ul_lon), parseFloat(this.props.parent.state.localeListInMap[i].ul_lat)]
          ]]
        }
      });
    }
    var boxLayers = new GeoJsonLayer({
        id: 'geojson-layer',
        data: boxData,
        lineWidthScale: 4,
        opacity: 0.4,
        filled: true,
        stroked: true,
        lineWidth: 2,
        lineWidthMinPixels: 2,
        wireframe: true,
        // autoHighlight: true,
        getLineColor: f => {
          var hex = f.color;
          hex = hex.slice(-6).padStart(6, '0');
          // convert to RGB
          return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0];
        },
        getFillColor: f => {
          var hex = f.color;
          hex = hex.slice(-6).padStart(6, '0');
          hex = hex + '1f';
          // convert to RGB
          return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0, 0];
        },
        pickable: true,
      });

      return boxLayers
  }

  genIconLayer(){
    // Safety check: ensure parent state is available
    if (!this.props.parent || !this.props.parent.state || !this.props.parent.state.localeListInMap) {
      return new IconLayer({
        id: 'icon-layer',
        data: [],
        pickable: true,
        iconAtlas: 'https://raw.githubusercontent.com/visgl/deck.gl-data/master/website/icon-atlas.png',
        iconMapping: 'https://raw.githubusercontent.com/visgl/deck.gl-data/master/website/icon-atlas.json',
        getIcon: d => 'marker',
        sizeScale: 15,
        getPosition: d => d.coordinates,
        getSize: d => 5,
        getColor: d => [Math.sqrt(d.exits), 140, 0]
      });
    }
    
    var iconData = []
    for(var i = 0; i<this.props.parent.state.localeListInMap.length; i++){
      iconData.push({
        'name': this.props.parent.state.localeListInMap[i].name,
        'color': parseInt(this.props.parent.state.localeListInMap[i].color).toString(16),
        'coordinates': [(parseFloat(this.props.parent.state.localeListInMap[i].ul_lon) + parseFloat(this.props.parent.state.localeListInMap[i].br_lon))/2, (parseFloat(this.props.parent.state.localeListInMap[i].ul_lat) + parseFloat(this.props.parent.state.localeListInMap[i].br_lat))/2],
        'meta': this.props.parent.state.localeListInMap[i]
      });
    }

    const ICON_MAPPING = {
      marker: {
        x: 0,
        y: 0,
        width: 57,
        height: 80,
        anchorY: 80,
        mask: true
      }
    };

    var iconLayers = new IconLayer({
      id: 'icon-layer',
      data: iconData,
      pickable: true,
      autoHighlight: true,
      iconAtlas: custom_pin,
      iconMapping: ICON_MAPPING,
      getIcon: d => 'marker',
  
      sizeScale: 15,
      getPosition: d => d.coordinates,
      getSize: d => 5,
      getColor: f => {
        var hex = f.color;
        hex = hex.slice(-6).padStart(6, '0');
        // convert to RGB
        return hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0, 0];
      },
      onClick: (info, event) => {
        if(info.object){
          console.log('index assigned normally');
          this.setState({
            popup_data:{
              show: true,
              lat: info.object.coordinates[1],
              lon: info.object.coordinates[0],
              meta: info.object.meta,
              index: info.index,
              x: info.x,
              y: info.y
            }
          });
          this.state.popup_data = {
            show: true,
            lat: info.object.coordinates[1],
            lon: info.object.coordinates[0],
            meta: info.object.meta,
            index: info.index,
            x: info.x,
            y: info.y
          }
        }else{
          console.log('index assigned -1');
          this.setState({
            popup_data:{
              show: false,
              lat: 0,
              lon: 0,
              meta: null,
              index: -1
            }
          });
          this.state.popup_data = {
            show: false,
            lat: 0,
            lon: 0,
            meta: null,
            index: -1
          }
        }
      }
    });

    return iconLayers;
  }
  
  genTextLayer(){
    // Safety check: ensure parent state is available
    if (!this.props.parent || !this.props.parent.state || !this.props.parent.state.localeListInMap) {
      return new TextLayer({
        id: 'TextLayer',
        data: [],
        pickable: true,
        getPosition: d => d.coordinates,
        getText: d => d.name,
        getSize: 16,
        getAngle: 0,
        getTextAnchor: 'middle',
        getAlignmentBaseline: 'center'
      });
    }
    
    var textData = []
    var charSet = ''
    for(var i = 0; i<this.props.parent.state.localeListInMap.length; i++){
      textData.push({
        'color': parseInt(this.props.parent.state.localeListInMap[i].color).toString(16),
        //'name': ' ' + this.props.parent.state.localeListInMap[i].name + ' ',
        'name': this.props.parent.state.localeListInMap[i].name.length >= 15 ? this.props.parent.state.localeListInMap[i].name.substring(0, 15)+ '...' : (this.props.parent.state.localeListInMap[i].name),
        'coordinates': [(parseFloat(this.props.parent.state.localeListInMap[i].ul_lon) + parseFloat(this.props.parent.state.localeListInMap[i].br_lon))/2, (parseFloat(this.props.parent.state.localeListInMap[i].ul_lat) + parseFloat(this.props.parent.state.localeListInMap[i].br_lat))/2]
      });
      if( this.props.parent.state.localeListInMap[i].name.length >= 15){
        charSet = charSet + this.props.parent.state.localeListInMap[i].name + '...';
      }else{
        charSet = charSet + this.props.parent.state.localeListInMap[i].name;
      }
    }
    var textLayers = new TextLayer({
      id: 'TextLayer',
      data: textData,
      background: true,
      getBackgroundColor: [255, 255, 255, 150],
      backgroundPadding: [15, 6, 15, 6],
      characterSet: charSet.split(''),
      // fontSettings: {
      //   'fontSize': 100,
      // },
      // fontWeight: 100,
      getAlignmentBaseline: 'top',
      getAngle: 0,
      // getColor: [255, 255, 255],
      getColor: f => {
        var hex = f.color;
        hex = hex.slice(-6).padStart(6, '0');
        // convert to RGB
        var rgb = hex ? hex.match(/[0-9a-f]{2}/g).map(x => parseInt(x, 16)) : [0, 0, 0];
        // add alpha value
        rgb.push(0.8 * 255); // 80% opacity
        return rgb;

      },
      getPosition: d => d.coordinates,
      getSize: 12,
      getText: d => d.name,
      getTextAnchor: 'middle',
      lineHeight: 1,
      sizeScale: 1,
      pickable: true,
    });

    return textLayers;
  }
  render() {
    // Safety check: ensure parent props are available
    if (!this.props.parent || !this.props.parent.refs || !this.props.parent.refs.localeMapState || !this.props.parent.refs.localeMapState.state) {
      console.log('📍 LocaleMapView: Parent props not ready, showing loading...');
      return (
        <div style={{ 
          position: "relative", 
          width: "100%", 
          height: "400px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          <div>Loading Map...</div>
        </div>
      );
    }

    const initialViewState = {
      longitude: this.props.parent.refs.localeMapState.state.center.lng,
      latitude: this.props.parent.refs.localeMapState.state.center.lat,
      zoom: this.props.parent.refs.localeMapState.state.zoom,
      bearing: 0,
      pitch: 0
    };

    const { map } = this.state;

    const boxLayers = this.genBoxData();
    const iconLayers = this.genIconLayer();
    const textLayers = this.genTextLayer();

    const mapContainerStyle = {
      position: "relative",
      width: "100%",
      height: this.state.height,
      zIndex: 1060,
      boxSizing: "border-box",
      // Clip children (style buttons, marker popup, tooltips) so they can never
      // widen the page and cause horizontal scroll jitter on the map view.
      overflow: "hidden"
    };

    // Fallback view if WebGL is not supported
    if (!this.state.webGLSupported) {
      return (
        <div style={mapContainerStyle}>
          <Box
            p="md"
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "100%",
              flexDirection: "column"
            }}
          >
            <Text size="xl" fw={600} c="red" mb="xs">
              WebGL not supported
            </Text>
            <Text ta="center">
              Your browser or device doesn't support WebGL, which is required to display the map.
              Please try using a different browser or update your graphics drivers.
            </Text>
          </Box>
        </div>
      );
    }

    return (
      <div style={mapContainerStyle}>
        <DeckGL
          id="map-deck-gl"
          initialViewState={initialViewState} 
          controller={true} 
          height="100%" 
          width="100%"
          position="absolute" 
          style={{
            zIndex: 1060,
            position: "absolute",
            top: 0,
            left: 0
          }}
          layers={[boxLayers, iconLayers, textLayers]}
          onError={(error) => {
            console.error("DeckGL error:", error);
            this.setState({ webGLSupported: false });
          }}
          getTooltip={({object}) => object && (object.properties?.name || object.name)}
          onViewStateChange={(viewState =>{
            if (this.state.popup_data.show) {
              this.setState({ popup_data: { ...this.state.popup_data, show: false } });
              this.state.popup_data.show = false;
            }
            if (viewState.interactionState){
              if (!viewState.interactionState.isDragging && !viewState.interactionState.isRotating){
                const viewport = new WebMercatorViewport(viewState.viewState);
                const ul = viewport.unproject([0, 0]);
                const br = viewport.unproject([this.state.width, this.state.height], false);
                
                const newBounds = {
                    ul: { lat: ul[1], lon: ul[0] },
                    br: { lat: br[1], lon: br[0] }
                };

                // update map's field here (center...)
                this.setState(prevState => ({
                    map: {
                        ...prevState.map,
                        zoom: viewState.viewState.zoom,
                        center: {
                            lon: viewState.viewState.longitude,
                            lat: viewState.viewState.latitude
                        },
                        bounds: newBounds
                    }
                }));
                
                // Push zoom/center up as well, not just the bounds. This view
                // and LocaleMapState hold the same values, and componentDidUpdate
                // copies LocaleMapState -> here on ANY difference; leaving the
                // shared copy stale meant the user's own pan/zoom was
                // immediately overwritten with the previous position on the
                // very next render, so the map position never actually moved.
                if (this.props.parent.refs.localeMapState) {
                    this.props.parent.refs.localeMapState.setState({
                        bounds: newBounds,
                        zoom: viewState.viewState.zoom,
                        center: {
                            lat: viewState.viewState.latitude,
                            lng: viewState.viewState.longitude
                        }
                    });
                    // Keep the direct-mutation copies this codebase relies on in
                    // step (LocaleMapState does the same in updateState).
                    this.props.parent.refs.localeMapState.state.bounds = newBounds;
                    this.props.parent.refs.localeMapState.state.zoom = viewState.viewState.zoom;
                    this.props.parent.refs.localeMapState.state.center = {
                        lat: viewState.viewState.latitude,
                        lng: viewState.viewState.longitude
                    };
                }

                clearTimeout(this.dragEndTimeout);
                this.dragEndTimeout = setTimeout(()=>{
                  this.onDragEnd();
                }, 500);
              }
            }
          })}
        >
          <Map 
            mapStyle={map.style} 
            mapboxAccessToken={Config.MBOX_KEY()} 
            attributionControl={false}
            style={{width: "100%", height: "100%"}}
          />
          {this.state.popup_data.show && (
            <div
              style={{
                position: 'absolute',
                left: this.state.popup_data.x,
                top: this.state.popup_data.y + 8,
                transform: 'translateX(-50%)',
                zIndex: 2000,
                pointerEvents: 'auto',
                backgroundColor: 'white',
                borderRadius: '8px',
                boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
                padding: '4px 8px'
              }}
              onPointerDown={e => e.stopPropagation()}
            >
              <Button.Group>
                <Tooltip label={Localization.strings.EDIT} position="left" withArrow>
                  <ActionIcon variant="subtle" onClick={()=>{
                    this.setState({popup_data: {
                      ...this.state.popup_data,
                      show: false
                    }});
                    this.editLocaleClicked(this.state.popup_data.index);
                    }}
                    disabled={!(this.props.root.getSameSchoolEdit() || this.props.root.getMyUserId() == this.state.popup_data.meta.user_id)}
                    aria-label="Edit">
                    <IconEdit size={18} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={Localization.strings.RETRIEVE_ANSWERS} position="bottom" withArrow>
                  <ActionIcon variant="subtle" onClick={()=>{
                    this.setState({popup_data: {
                      ...this.state.popup_data,
                      show: false
                    }});
                    this.retrieveAnswerClicked(this.state.popup_data.index);
                    }}
                    aria-label="Retrieve">
                    <IconReportAnalytics size={18} />
                  </ActionIcon>
                </Tooltip>
                <Tooltip label={Localization.strings.DELETE} position="right" withArrow>
                  <ActionIcon variant="subtle" color="red" onClick={()=>{
                    this.setState({popup_data: {
                      ...this.state.popup_data,
                      show: false
                    }});
                    this.deleteLocaleClicked(this.state.popup_data.meta.id);
                    }}
                    disabled={!(this.props.root.getSameSchoolEdit() || this.props.root.getMyUserId() == this.state.popup_data.meta.user_id)}
                    aria-label="Del">
                    <IconTrash size={18} />
                  </ActionIcon>
                </Tooltip>
              </Button.Group>
            </div>
          )}
          <Button.Group style={{top:10, left:10, position:"relative", width:"fit-content"}}>
            <Button size="xs" variant={this.state.map._style=="mono"? "default":"filled"} onClick={
              () => {
                this.setState({
                  map:{
                    ...this.state.map,
                    style: Config.MBOX_STYLE(Localization.strings.getLanguage()).mono,
                    _style: "mono"
                  }
                });
              }
            }>{Localization.strings.MAP_MONO}</Button>
            <Button size="xs" variant={this.state.map._style=="satellite"? "default":"filled"} onClick={
              () => {
                this.setState({
                  map:{
                    ...this.state.map,
                    style: Config.MBOX_STYLE(Localization.strings.getLanguage()).satellite,
                    _style: "satellite"
                  }
                });
              }
            }>{Localization.strings.MAP_SATELLITE}</Button>
            <Button size="xs" variant={this.state.map._style=="cartoon"? "default":"filled"} onClick={
              () => {
                this.setState({
                  map:{
                    ...this.state.map,
                    style: Config.MBOX_STYLE(Localization.strings.getLanguage()).cartoon,
                    _style: "cartoon"
                  }
                });
              }
            }>{Localization.strings.MAP_VECTOR}</Button>
          </Button.Group>
        </DeckGL>
      </div>
    )
  ;}
}
export default LocaleMapView;
